<template>
  <div>
    <JobPostConfirmTransitionText v-if="!draft.isPrepopulatedFromReservation" :isAiGenerated="draft.aiGenerated" />
    <h2 class="text-h2 my-32">
      {{
        draft.isPrepopulatedFromReservation
          ? 'Let’s help you find a caregiver by sharing your Back-Up Care request'
          : 'Review your job'
      }}
    </h2>

    <p v-if="draft.isPrepopulatedFromReservation" class="mb-28 md:mb-16">
      Review the information below to make sure it’s accurate. This will be shared with caregivers who can help fulfill your
      request.
    </p>

    <form @submit.prevent="submit">
      <Card class="flex flex-col sm:p-24">
        <RouteStepperReview :reviewSections="reviewSections" />
        <p class="text-xs leading-5 text-grey-text-subdued mb-16 border-t-1 border-solid border-grey-divider-subdued pt-24">
          OpenAI used the information you have provided about this job to generate your job description. The generated output may
          contain errors. You agree to review it before publishing and understand you are responsible for the information you
          post.
          <a
            class="underline"
            href="https://support.sittercity.com/hc/en-us/articles/23699389621019-Write-your-job-description-with-OpenAI"
            target="_blank"
          >
            Learn more
          </a>
        </p>
        <Button :loading="isLoading" type="submit" size="medium" class="ml-auto w-full md:max-w-max">{{
          draft.isPrepopulatedFromReservation ? 'Looks good, share with caregivers' : 'Share with sitters'
        }}</Button>
      </Card>
    </form>
  </div>
</template>

<script lang="ts">
import Button from '@/components/button/Button.vue';
import RouteStepperReview from '@/components/route_stepper/RouteStepperReview.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import Card from '../../../components/card/Card.vue';
import JobPostConfirmTransitionText from './JobPostConfirmTransitionText.vue';
import { PropType } from 'vue';
import { JobPostMutationInput } from '@/store/modules/job/types';
import { JobPostDraft, ScheduleTypes } from '../types';
import { redirect } from '@/helpers/redirects';
import { Account } from '@/models/account';

export default {
  name: 'JobPostConfirm',
  components: { Button, RouteStepperReview, Card, JobPostConfirmTransitionText },
  props: {
    stepsData: {
      type: Array as PropType<ReviewSection[]>,
      required: true,
    },
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    account(): Account {
      return this.$store.getters['account/getAccount'];
    },
    headlineReviewSection(): ReviewSection[] {
      const headline = this.draft.headlineReviewSection;
      return headline ? [headline] : [];
    },
    reviewSections(): ReviewSection[] {
      const sections = this.stepsData.filter((reviewSection) => reviewSection.title && reviewSection.editPath);
      return [...this.headlineReviewSection, ...sections];
    },
  },
  methods: {
    async submit(): Promise<void> {
      this.isLoading = true;
      const jobPostMutationInput: JobPostMutationInput = {
        title: this.draft.title,
        description: this.draft.description,
        ageGroups: this.draft.ageGroups,
        requirements: this.draft.requirements,
        languages: this.draft.languages,
        responsibilities: this.draft.responsibilities,
        location: this.draft.location,
        numberOfChildren: this.draft.numberOfChildren,
        paymentRate: this.draft.paymentRate,
        schedule: this.draft.schedule,
        aiGenerated: this.draft.aiGenerated,
        careType: this.draft.scheduleType === ScheduleTypes.NANNY ? 'NANNY' : 'BABYSITTER',
      };

      try {
        const response = await this.$store.dispatch('job/createJobPost', jobPostMutationInput);
        if (!!response) {
          if (this.draft.jobPostReturnPath) {
            const url = new URL(this.draft.jobPostReturnPath, window.location.origin);
            redirect(url.href);
          } else {
            redirect('/account/seekers/dashboard');
          }
        }
      } catch (error) {
        console.error('Failed to create job post:', error);
        this.isLoading = false;
      }
    },
  },
};
</script>
